// The ticket sidebar: who the ticket is for, who owns it, and everything linked to it.
//
// All of it arrives in the one GET /api/tickets/:publicRef response (docs/API.md §3.2),
// so nothing here fetches, except the store panel under Location (its Mesh stations, read
// by StorePanel itself, lazily). Objects are null rather than partially populated — an
// absent organization is `null`, never `{ id: null }` — so a plain truthiness check is
// the right test throughout.

import { Suspense, lazy, useState } from 'react';

import { typeLabel } from '../../pages/tickets/ticket-type.js';
import { formatDateTime, CHANNEL_LABELS } from './format.js';

// The New Ticket screen's store panel (Inventory link, Mesh stations), lazy so the ticket
// view's eager JS keeps only the chunk reference.
const StorePanel = lazy(() => import('../create/StorePanel.jsx'));

function Section({ title, children, action }) {
  return (
    <section className="border-t border-line px-4 py-3 first:border-t-0">
      <h3 className="flex items-center justify-between text-xs font-semibold uppercase tracking-wide text-ink-secondary">
        {title}
        {action}
      </h3>
      <div className="mt-2 space-y-1.5 text-sm">{children}</div>
    </section>
  );
}

function Field({ label, children }) {
  if (children === null || children === undefined || children === '') return null;
  return (
    <div className="flex gap-2">
      <dt className="w-28 shrink-0 text-ink-secondary">{label}</dt>
      <dd className="min-w-0 break-words text-ink">{children}</dd>
    </div>
  );
}

function Empty({ children }) {
  return <p className="text-sm text-ink-muted">{children}</p>;
}

/** A `custom_fields` value, whose JSON type follows the field's `data_type` (§1.9). */
function customValue(value) {
  if (value === null || value === undefined) return <span className="text-ink-muted">-</span>;
  if (typeof value === 'boolean') return value ? 'Yes' : 'No';
  if (Array.isArray(value)) return value.length ? value.join(', ') : <span className="text-ink-muted">-</span>;
  return String(value);
}

function humanizeKey(key) {
  return key.replace(/_/g, ' ').replace(/^./, (character) => character.toUpperCase());
}

/**
 * Does this field's value tell an agent anything?
 *
 * AN UNTICKED CHECKBOX DOES NOT. A ported ticket carries every field on its Zendesk
 * form, and those forms are long: one real ticket (#39016) arrived with 42 custom
 * values of which 39 were `false`, so the panel rendered a wall of forty "No"s with
 * three real answers buried in it. Zendesk does not show that wall because its forms
 * hide fields behind conditions; ours has the same conditions imported but applies them
 * to the CREATE screen, not to a ticket that already exists.
 *
 * A ticked box is kept -- `true` is somebody's answer. So is an explicit empty string on
 * a text field, which is why only `false` and null/undefined are dropped rather than
 * everything falsy.
 */
function carriesAnAnswer(value) {
  if (value === false) return false;
  if (value === null || value === undefined) return false;
  if (Array.isArray(value)) return value.length > 0;
  return String(value).trim() !== '';
}

export default function TicketMeta({ ticket }) {
  const allFieldKeys = Object.keys(ticket.custom_fields ?? {});
  const customFieldKeys = allFieldKeys.filter(
    (key) => carriesAnAnswer(ticket.custom_fields[key]),
  );
  const hiddenFieldCount = allFieldKeys.length - customFieldKeys.length;
  const [showAllFields, setShowAllFields] = useState(false);
  const shownFieldKeys = showAllFields ? allFieldKeys : customFieldKeys;

  return (
    <aside className="divide-y divide-line rounded-lg border border-line bg-surface">
      <Section title="Requester">
        <dl className="space-y-1.5">
          <Field label="Name">{ticket.customer.name}</Field>
          <Field label="Email">
            <a href={`mailto:${ticket.customer.email}`} className="text-brand-700 dark:text-brand-300 hover:underline">
              {ticket.customer.email}
            </a>
          </Field>
          <Field label="Phone">{ticket.customer.phone}</Field>
          <Field label="Company">{ticket.customer.company}</Field>
        </dl>
      </Section>

      <Section title="Organization">
        {ticket.organization ? (
          <dl className="space-y-1.5">
            <Field label="Name">
              {ticket.organization.name}
              {ticket.organization.is_active === false && (
                <span className="ml-2 text-xs text-ink-muted">(inactive)</span>
              )}
            </Field>
            <Field label="Domains">{ticket.organization.domains?.join(', ')}</Field>
          </dl>
        ) : (
          <Empty>No organization, a one-off caller.</Empty>
        )}
      </Section>

      <Section title="Location">
        {ticket.location ? (
          <dl className="space-y-1.5">
            <Field label="Site">{ticket.location.name}</Field>
            <Field label="Account #">{ticket.location.account_number}</Field>
            <Field label="Address">{ticket.location.address}</Field>
            <Field label="Phone">{ticket.location.phone}</Field>
            <Field label="Time zone">{ticket.location.timezone}</Field>
          </dl>
        ) : (
          <Empty>No location on this ticket.</Empty>
        )}
        {ticket.location && (
          <Suspense fallback={null}>
            <StorePanel location={ticket.location} />
          </Suspense>
        )}
      </Section>

      <Section title="Assignment">
        <dl className="space-y-1.5">
          <Field label="Assignee">
            {ticket.assignee ? (
              <>
                {ticket.assignee.display_name}
                <span className="block text-xs text-ink-muted">{ticket.assignee.email}</span>
              </>
            ) : (
              <span className="text-ink-muted">Unassigned</span>
            )}
          </Field>
          <Field label="Queue">{ticket.queue?.name}</Field>
          {/* Always rendered: "Not categorised" is an answer, and a missing row would
              read as a screen that does not know about types. */}
          <Field label="Type">
            {ticket.type ? typeLabel(ticket.type) : <span className="text-ink-muted">{typeLabel(null)}</span>}
          </Field>
          <Field label="Form">{ticket.form?.name}</Field>
          <Field label="Brand">{ticket.brand?.name}</Field>
          <Field label="Channel">{CHANNEL_LABELS[ticket.channel] ?? ticket.channel}</Field>
          <Field label="Opened by">{ticket.created_by?.display_name}</Field>
        </dl>
      </Section>

      <Section title="Timeline">
        <dl className="space-y-1.5">
          <Field label="Created">{formatDateTime(ticket.created_at)}</Field>
          <Field label="Updated">{formatDateTime(ticket.updated_at)}</Field>
          <Field label="Acknowledged">{formatDateTime(ticket.acknowledged_at)}</Field>
          <Field label="First reply">{formatDateTime(ticket.first_response_at)}</Field>
          <Field label="Resolved">{formatDateTime(ticket.resolved_at)}</Field>
        </dl>
      </Section>

      {/* sla_resolution_due_at is permanently null (migration 014 — there is no
          resolution SLA in the live configuration), so the section's presence and
          its one due-date row both key off the first-response clock instead.

          A deadline is only worth showing while it is still a deadline. Once the
          clock is satisfied the measurement is finished, and a phone ticket is
          satisfied at the instant it is created (design §2.2) — so this panel used
          to tell an agent "First reply due <tomorrow>" about a call they had just
          answered on the spot. The data was right and the display was wrong:
          sla_events still records the due date, and reporting still reads it. */}
      {(ticket.sla_policy || ticket.sla_first_response_due_at) && (
        <Section title="SLA">
          <dl className="space-y-1.5">
            <Field label="Policy">{ticket.sla_policy?.name}</Field>
            {ticket.sla_first_response_satisfied_at ? (
              <Field label="First reply">
                Met {formatDateTime(ticket.sla_first_response_satisfied_at)}
              </Field>
            ) : (
              <Field label="First reply due">
                <span className={ticket.sla_breached ? 'font-semibold text-red-700 dark:text-red-400' : undefined}>
                  {formatDateTime(ticket.sla_first_response_due_at)}
                  {ticket.sla_breached && ' (breached)'}
                </span>
              </Field>
            )}
          </dl>
        </Section>
      )}

      <Section title="Tags">
        {ticket.tags.length > 0 ? (
          <div className="flex flex-wrap gap-1.5">
            {ticket.tags.map((tag) => (
              <span key={tag} className="rounded bg-sunken px-2 py-0.5 text-xs text-ink-secondary ring-1 ring-inset ring-line">
                {tag}
              </span>
            ))}
          </div>
        ) : (
          <Empty>No tags.</Empty>
        )}
      </Section>

      {shownFieldKeys.length > 0 && (
        <Section title="Fields">
          <dl className="space-y-1.5">
            {shownFieldKeys.map((key) => (
              <Field key={key} label={humanizeKey(key)}>
                {customValue(ticket.custom_fields[key])}
              </Field>
            ))}
          </dl>
          {/* The count is named rather than the rest being silently dropped: an agent
              who needs to know a box is unticked has to be able to find out, and a
              panel that quietly shows a subset is worse than one that shows a wall. */}
          {hiddenFieldCount > 0 && (
            <button
              type="button"
              onClick={() => setShowAllFields((shown) => !shown)}
              className="mt-2 text-xs text-brand-700 hover:underline dark:text-brand-300"
            >
              {showAllFields
                ? 'Hide unticked fields'
                : `Show ${hiddenFieldCount} unticked field${hiddenFieldCount === 1 ? '' : 's'}`}
            </button>
          )}
        </Section>
      )}

      {/* CCs are customers and followers are staff, and the difference decides who sees
          a customer-facing reply. Labelled rather than left to be inferred from the
          section name — same reasoning as the thread styling (SPEC.md §7.4). */}
      <Section title="CC">
        {ticket.ccs.length > 0 ? (
          <>
            <p className="text-xs text-ink-muted">Customers: they receive customer-facing replies.</p>
            <ul className="space-y-1">
              {ticket.ccs.map((cc) => (
                <li key={cc.customer.id}>
                  {cc.customer.name}
                  <span className="block text-xs text-ink-muted">{cc.customer.email}</span>
                </li>
              ))}
            </ul>
          </>
        ) : (
          <Empty>No CCs.</Empty>
        )}
      </Section>

      <Section title="Followers">
        {ticket.followers.length > 0 ? (
          <>
            <p className="text-xs text-ink-muted">Staff: notified internally, not emailed to the customer.</p>
            <ul className="space-y-1">
              {ticket.followers.map((follower) => (
                <li key={follower.user.id}>
                  {follower.user.display_name}
                  <span className="block text-xs text-ink-muted">{follower.user.email}</span>
                </li>
              ))}
            </ul>
          </>
        ) : (
          <Empty>No followers.</Empty>
        )}
      </Section>

      {/* Parent, children and related tickets live in RelatedTickets.jsx, rendered
          beneath this panel by Detail.jsx — they carry actions (the (+) for a child,
          link/unlink) and this panel is read-only. */}
    </aside>
  );
}
